import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "./api";
import { useAuth } from "./auth";

const EntityContext = createContext();

export function EntityProvider({ children }) {
  const { authed, account } = useAuth() || {};
  const [entities, setEntities] = useState([]);
  const [activeEntityId, setActiveEntityId] = useState(() => {
    try {
      return localStorage.getItem("lapen_active_entity") || "all";
    } catch {
      return "all";
    }
  });
  const [loading, setLoading] = useState(true);
  const prevAccountRef = useRef(account?.id);

  const loadEntities = useCallback(async () => {
    try {
      const res = await api.get("/entities");
      const list = res.data || [];
      setEntities(list);
      setActiveEntityId((prev) => {
        if (prev === "all") return "all";
        const exists = list.some((e) => String(e.id) === String(prev));
        if (!exists) {
          try {
            localStorage.removeItem("lapen_active_entity");
          } catch {}
          return "all";
        }
        return prev;
      });
    } catch (err) {
      console.error("Failed to load entities:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) {
      if (prevAccountRef.current && prevAccountRef.current !== account?.id) {
        setActiveEntityId("all");
        try {
          localStorage.removeItem("lapen_active_entity");
        } catch {}
      }
      prevAccountRef.current = account?.id;
      loadEntities();
    } else {
      setEntities([]);
      setActiveEntityId("all");
    }
  }, [authed, account?.id, loadEntities]);

  const switchEntity = (id) => {
    setActiveEntityId(id);
    try {
      localStorage.setItem("lapen_active_entity", id);
    } catch {
      // Ignore
    }
  };

  const activeEntity = entities.find((e) => e.id === activeEntityId) || null;

  return (
    <EntityContext.Provider
      value={{
        entities,
        activeEntityId,
        activeEntity,
        switchEntity,
        reloadEntities: loadEntities,
        loading,
      }}
    >
      {children}
    </EntityContext.Provider>
  );
}

export function useEntity() {
  const context = useContext(EntityContext);
  if (!context) {
    throw new Error("useEntity must be used within an EntityProvider");
  }
  return context;
}
